import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DropDownSelectorValues,
  ProjectStatus as ProjectStatusEnum,
  SubPage,
  Tab,
} from '@symfa-inc/providence-verizon-types';
import { Spin } from 'antd';
import { HttpService } from '@core/services';
import { EquipmentHttpService, LeasingHttpService } from '@core/services/http';
import {
  canUserEditByProjectStatusAndRoles,
  getProjectBundleIDParamFromUrl,
} from '@core/utils/methods';
import { EditablePage, LeasingEquipment } from '@shared/components';
import { ScopingActions } from '@store/actions';
import {
  DDVSelectors,
  LeasingSelectors,
  ProjectSelectors,
  ScopingSelectors,
  UserSelectors,
} from '@store/selectors';

type ScopingLeasingEquipmentTabProps = {
  isActive?: boolean;
};

export const ScopingLeasingEquipmentTab: FC<
  ScopingLeasingEquipmentTabProps
> = ({ isActive }) => {
  const dispatch = useDispatch();
  const [projectBundleID] = getProjectBundleIDParamFromUrl();
  const isLoading = useSelector(
    ScopingSelectors.getTabLoadingState('leaseEquipment'),
  );
  const isDataLoaded = useSelector(
    ScopingSelectors.getTabLoadedState('leaseEquipment'),
  );
  const canEdit = useSelector(
    UserSelectors.canEdit(
      'engineering',
      SubPage.EngineeringScoping,
      Tab.LeaseEquipment,
    ),
  );

  const userRoles = useSelector(UserSelectors.getUserRoles);
  const projectStatusId = useSelector(ProjectSelectors.getProjectStatus);
  const projectStatus = useSelector(
    DDVSelectors.getCurrentDDVValueById<ProjectStatusEnum>(
      DropDownSelectorValues.ProjectStatus,
      projectStatusId,
    ),
  );
  const { equipmentTypeNames, ...leasingEquipmentData } = useSelector(
    LeasingSelectors.getLeasingEquipmentData,
  );

  useEffect(() => {
    if (isActive && !isDataLoaded && !isLoading) {
      dispatch(
        ScopingActions.setTabLoading.done({
          tabName: 'leaseEquipment',
          isLoading: true,
        }),
      );

      const loadLeaseEquipmentTab = async (): Promise<void> => {
        try {
          await HttpService.getHttpRequests(
            LeasingHttpService,
          ).getLeasingEquipmentTabData(projectBundleID);
          dispatch(
            ScopingActions.setTabLoaded.done({
              tabName: 'leaseEquipment',
              isLoaded: true,
            }),
          );
        } catch {
          dispatch(
            ScopingActions.setTabError.done({
              tabName: 'leaseEquipment',
              hasError: true,
            }),
          );
        }
      };

      loadLeaseEquipmentTab();
    }
  }, [dispatch, isActive, isDataLoaded, isLoading, projectBundleID]);

  useEffect(() => {
    if (isActive && isDataLoaded && equipmentTypeNames?.length) {
      HttpService.getHttpRequests(EquipmentHttpService).getEquipmentsByTypes(
        equipmentTypeNames,
      );
    }
  }, [equipmentTypeNames, isActive, isDataLoaded]);

  if (isLoading || !isDataLoaded) {
    return (
      <div className="prov-scoping-leasing-equipment-tab loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <EditablePage
      editableComponent={LeasingEquipment}
      canUserEdit={
        canEdit && canUserEditByProjectStatusAndRoles(projectStatus, userRoles)
      }
      data={{ equipmentTypeNames, ...leasingEquipmentData }}
    />
  );
};
